import { useCallback, useEffect, useRef } from 'react';

/**
 * Polling hook, ported from extensions/default/src/hooks/usePolling.ts on
 * `develop` with design defect 9 fixed in all three of its parts:
 *
 *   9a  startPolling(cb) silently DISCARDED cb whenever a timer already
 *       existed (`if (pollIntervalRef.current || timeoutRef.current) return`
 *       came before any use of the argument).
 *   9b  A setTimeout(interval) that then armed a setInterval(interval), so the
 *       first tick landed at 2x the interval.
 *   9c  The setInterval closure captured `callback` at arm time; a re-render
 *       with a new callback identity rebuilt `startPolling` but not the live
 *       timer, so the stale callback kept firing.
 *
 * The timeout ref is gone entirely — it existed only to produce 9b.
 */
export const usePolling = (callback: () => Promise<void>, interval = 5000) => {
  const intervalRef = useRef<number | undefined>(undefined);
  const callbackRef = useRef(callback);
  const periodRef = useRef(interval);

  // 9c: one indirection, so the live timer always calls the current callback.
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== undefined) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  }, []);

  const arm = useCallback(() => {
    // 9b: no leading setTimeout. First tick at 1x interval.
    intervalRef.current = window.setInterval(() => {
      void callbackRef.current();
    }, periodRef.current);
  }, []);

  const startPolling = useCallback(
    (override?: () => Promise<void>) => {
      // 9a: the override is applied BEFORE the already-running check, so a
      // caller that re-arms with a new callback is never silently ignored.
      if (override) {
        callbackRef.current = override;
      }

      if (intervalRef.current !== undefined) {
        return;
      }

      arm();
    },
    [arm]
  );

  /**
   * The period half of 9c, which the callback half left behind: setInterval captures its
   * delay when it is armed, and startPolling early-returns while a timer is live, so a
   * changed `interval` reached the running timer through no path at all — it would have
   * taken effect only after some unrelated stop and start. Re-arm instead.
   *
   * Nothing passes a second argument today; this is a latent hazard rather than an
   * observed one, and it is fixed because the sibling case already was.
   */
  useEffect(() => {
    periodRef.current = interval;

    if (intervalRef.current === undefined) {
      return;
    }

    window.clearInterval(intervalRef.current);
    arm();
  }, [interval, arm]);

  useEffect(() => stopPolling, [stopPolling]);

  return { startPolling, stopPolling };
};
